import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { FormField } from './FormField';

// #14005 — Checkbox + Radio/RadioGroup boolean/choice controls.
//
// Conventions followed: label-to-the-right association via htmlFor/id (matching
// Input.tsx's label idiom); indigo accent + visible focus ring matching the
// existing ad-hoc checkbox usages across the admin portal (e.g. CaseDetail.tsx,
// IncidentDomainList.tsx: `text-indigo-600` + `focus:ring-indigo-500`). Because
// no `@tailwindcss/forms` plugin is configured in any portal's tailwind.config.ts
// (`plugins: []`), `text-indigo-600` alone does not colour the native checked-state
// fill — `accent-indigo-600` (Tailwind 3.1+, no plugin required) is added so the
// "indigo checked state" requirement actually renders, not just matches convention.

// ── Checkbox ──────────────────────────────────────────────────────────────────────

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label text, rendered to the right of the box and associated via htmlFor/id (clickable). */
  label?: string;
  /**
   * Renders the checkbox in the "indeterminate" (partial-selection) visual state.
   * Not a native HTML attribute — `indeterminate` can only be set as a DOM property,
   * never via an attribute — so it is applied imperatively via a ref effect.
   */
  indeterminate?: boolean;
}

/**
 * Checkbox — native `type="checkbox"` with an indigo checked state, a visible focus
 * ring, and a clickable label to the right (associated via htmlFor/id, same idiom as
 * `Input`). Supports the native indeterminate visual state via a ref effect, since
 * `indeterminate` is DOM-property-only.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, id, className = '', indeterminate = false, ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement | null>(null);
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    useEffect(() => {
      if (internalRef.current) {
        internalRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <div className="flex items-center">
        <input
          ref={(node) => {
            internalRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          type="checkbox"
          id={inputId}
          className={`h-4 w-4 rounded border-gray-300 text-indigo-600 accent-indigo-600
            focus:outline-none focus:ring-2 focus:ring-indigo-500/50
            disabled:cursor-not-allowed disabled:opacity-50
            ${className}`}
          {...props}
        />
        {label && (
          <label htmlFor={inputId} className="ml-2 block text-sm text-gray-700">
            {label}
          </label>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';

// ── Radio ─────────────────────────────────────────────────────────────────────────

export interface RadioProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label text, rendered to the right and associated via htmlFor/id (clickable). */
  label: string;
}

/**
 * Radio — a single native `type="radio"` control with a clickable label to the
 * right. Used standalone or composed by `RadioGroup` below; grouping (and the
 * resulting native keyboard arrow-key navigation) comes from sharing the same
 * `name` across sibling radios, which is a browser default behaviour — no extra
 * keyboard handling is implemented here.
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ label, id, className = '', ...props }, ref) => {
    const inputId = id || `radio-${label.toLowerCase().replace(/\s+/g, '-')}`;
    return (
      <div className="flex items-center">
        <input
          ref={ref}
          type="radio"
          id={inputId}
          className={`h-4 w-4 rounded-full border-gray-300 text-indigo-600 accent-indigo-600
            focus:outline-none focus:ring-2 focus:ring-indigo-500/50
            disabled:cursor-not-allowed disabled:opacity-50
            ${className}`}
          {...props}
        />
        <label htmlFor={inputId} className="ml-2 block text-sm text-gray-700">
          {label}
        </label>
      </div>
    );
  },
);

Radio.displayName = 'Radio';

// ── RadioGroup ────────────────────────────────────────────────────────────────────

export interface RadioGroupOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  /**
   * Shared `name` for every radio in the group. Native radios sharing a `name`
   * are mutually exclusive and give keyboard arrow-key navigation between
   * options for free — no custom keydown handling is implemented here.
   */
  name: string;
  /** The currently-selected option's value (controlled). */
  value: string;
  /** Fires with the newly-selected option's value. */
  onChange: (value: string) => void;
  options: RadioGroupOption[];
  /** Group-level label, rendered via FormField chrome above the radios. */
  label?: string;
  required?: boolean;
  /** Group-level help text, rendered via FormField chrome below the radios. */
  help?: string;
  /** Group-level error text, rendered via FormField chrome below the radios. */
  error?: string;
  /** Disables every radio in the group. */
  disabled?: boolean;
}

/**
 * RadioGroup — name-grouped radios (single selection). Options render as native
 * `<input type="radio">` sharing `name`, so the browser gives keyboard arrow-key
 * navigation between options for free (no custom keydown handling here). The
 * group-level label/help/error chrome is provided by `FormField`, with the radios
 * rendered as its control content via the render-prop form.
 *
 * Because a radiogroup container isn't a "labelable" target for `<label for>`
 * (only form controls like input/select/textarea/button are), the visible label
 * text FormField renders is mirrored onto the group container via `aria-label`
 * (WCAG 2.5.3 / ARIA16 technique) so the accessible name resolves correctly
 * regardless of the `<label for>` quirk.
 */
export function RadioGroup({
  name,
  value,
  onChange,
  options,
  label,
  required,
  help,
  error,
  disabled,
}: RadioGroupProps) {
  return (
    <FormField label={label} required={required} help={help} error={error}>
      {(controlProps) => (
        <div
          id={controlProps.id}
          role="radiogroup"
          aria-label={label}
          aria-describedby={controlProps['aria-describedby']}
          aria-invalid={controlProps['aria-invalid']}
          className="space-y-2"
        >
          {options.map((option) => (
            <Radio
              key={option.value}
              name={name}
              label={option.label}
              id={`${name}-${option.value}`}
              value={option.value}
              checked={value === option.value}
              disabled={disabled || option.disabled}
              onChange={() => onChange(option.value)}
            />
          ))}
        </div>
      )}
    </FormField>
  );
}
